import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import apiClient from '../api';
import { useAuth } from '../context/AuthContext';

const TYPE_LABEL = {
  all: 'All',
  weekly_checklist: 'Weekly checklists',
  time_off: 'Time off',
  resume: 'Resumes',
};

function openPath(card) {
  const id = card.sourceId;
  if (card.source === 'resume') return `/recruitment?id=${id}`;
  if (card.source === 'time_off') return `/forms-management?tab=0&id=${id}`;
  return `/forms-management?tab=2&id=${id}`;
}

function cardTime(card) {
  return Math.max(...card.reasons.map((reason) => new Date(reason.updatedAt || reason.createdAt || 0).getTime()));
}

function cardIsHigh(card) {
  return card.reasons.some((reason) => reason.severity === 'high');
}

function groupFlags(flags) {
  const groups = new Map();
  for (const flag of flags) {
    const key = `${flag.source}:${flag.sourceId}`;
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        source: flag.source,
        sourceId: flag.sourceId,
        personName: flag.personName || 'Unknown',
        review: flag.review || { reviewed: false, label: 'Not reviewed yet' },
        reasons: [],
      });
    }
    groups.get(key).reasons.push(flag);
  }
  return [...groups.values()];
}

function matchesFilters(card, filters) {
  if (filters.type !== 'all' && card.source !== filters.type) return false;
  if (filters.review === 'pending' && card.review.reviewed) return false;
  if (filters.review === 'reviewed' && !card.review.reviewed) return false;
  if (filters.severity === 'high' && !cardIsHigh(card)) return false;
  const query = filters.search.trim().toLowerCase();
  if (!query) return true;
  const text = [card.personName, ...card.reasons.map((reason) => reason.message)].join(' ').toLowerCase();
  return text.includes(query);
}

function sortCards(cards, sort) {
  return [...cards].sort((a, b) => {
    if (sort === 'newest') return cardTime(b) - cardTime(a);
    if (sort === 'oldest') return cardTime(a) - cardTime(b);
    if (sort === 'name') return a.personName.localeCompare(b.personName);
    if (a.review.reviewed !== b.review.reviewed) return a.review.reviewed ? 1 : -1;
    if (cardIsHigh(a) !== cardIsHigh(b)) return cardIsHigh(a) ? -1 : 1;
    return cardTime(b) - cardTime(a);
  });
}

export default function NeedsALookPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [type, setType] = useState('all');
  const [review, setReview] = useState('all');
  const [severity, setSeverity] = useState('all');
  const [sort, setSort] = useState('pending');
  const [search, setSearch] = useState('');

  const load = async ({ silent } = {}) => {
    if (!silent) setError('');
    try {
      const res = await apiClient.get('/anomalies', { params: { status: 'open' } });
      setFlags(res.data?.data || []);
    } catch (err) {
      setError('Could not load flags.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const onVisible = () => {
      if (!document.hidden) load({ silent: true });
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  const scan = async () => {
    setScanning(true);
    setError('');
    setNotice('');
    try {
      const companyId = user?.company?._id || user?.company;
      const res = await apiClient.post('/anomalies/scan', companyId ? { companyId } : {});
      const checked = res.data?.checked || {};
      setNotice(`Checked ${checked.resumes || 0} resumes, ${checked.checklists || 0} checklists, and ${checked.timeOff || 0} time-off requests.`);
      await load({ silent: true });
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not check recent records.');
    } finally {
      setScanning(false);
    }
  };

  const dismiss = async (event, flag) => {
    event.stopPropagation();
    try {
      await apiClient.post(`/anomalies/${flag._id}/dismiss`);
      setFlags((rows) => rows.filter((row) => row._id !== flag._id));
    } catch (err) {
      setError('Could not dismiss that flag.');
    }
  };

  const cards = useMemo(() => {
    const filters = { type, review, severity, search };
    return sortCards(groupFlags(flags).filter((card) => matchesFilters(card, filters)), sort);
  }, [flags, type, review, severity, search, sort]);

  return (
    <Container maxWidth="xl" disableGutters sx={{ pt: 0, pb: { xs: 1, md: 2 } }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#E94F37' }}>Needs a look</Typography>
          <Typography variant="body2" color="text.secondary">
            Odd resumes, time off, and weekly checklists. Saving still goes through.
          </Typography>
        </Box>
        <Button variant="contained" onClick={scan} disabled={scanning}>
          {scanning ? 'Checking…' : 'Check recent records'}
        </Button>
      </Stack>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          <TextField
            size="small"
            label="Search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Name or reason"
            sx={{ flex: 1.4 }}
          />
          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel>Type</InputLabel>
            <Select label="Type" value={type} onChange={(event) => setType(event.target.value)}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="weekly_checklist">Weekly checklists</MenuItem>
              <MenuItem value="time_off">Time off</MenuItem>
              <MenuItem value="resume">Resumes</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel>Review</InputLabel>
            <Select label="Review" value={review} onChange={(event) => setReview(event.target.value)}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="pending">Not reviewed yet</MenuItem>
              <MenuItem value="reviewed">Already reviewed</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel>Severity</InputLabel>
            <Select label="Severity" value={severity} onChange={(event) => setSeverity(event.target.value)}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="high">High only</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel>Sort</InputLabel>
            <Select label="Sort" value={sort} onChange={(event) => setSort(event.target.value)}>
              <MenuItem value="pending">Not reviewed first</MenuItem>
              <MenuItem value="newest">Newest</MenuItem>
              <MenuItem value="oldest">Oldest</MenuItem>
              <MenuItem value="name">Name</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert>}

      {loading ? (
        <CircularProgress />
      ) : flags.length === 0 ? (
        <Typography color="text.secondary">Nothing needs a look.</Typography>
      ) : cards.length === 0 ? (
        <Typography color="text.secondary">Nothing matches these filters.</Typography>
      ) : (
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
            {TYPE_LABEL[type] || 'All'}
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              {cards.length}
            </Typography>
          </Typography>
          <Stack spacing={1.5}>
            {cards.map((card) => (
              <Paper
                key={card.key}
                sx={{ p: 2, cursor: 'pointer' }}
                onClick={() => navigate(openPath(card))}
              >
                <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mb: 0.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{card.personName}</Typography>
                  {type === 'all' && (
                    <Chip size="small" variant="outlined" label={TYPE_LABEL[card.source]} />
                  )}
                  <Chip
                    size="small"
                    label={card.review.label}
                    color={card.review.reviewed ? 'success' : 'warning'}
                    variant={card.review.reviewed ? 'outlined' : 'filled'}
                  />
                  {cardIsHigh(card) && <Chip size="small" color="error" label="High" />}
                </Stack>
                <Stack spacing={0.75} sx={{ mt: 1 }}>
                  {card.reasons.map((reason) => (
                    <Stack key={reason._id} direction="row" alignItems="center" spacing={1}>
                      <Typography variant="body2" sx={{ flex: 1 }}>{reason.message}</Typography>
                      <Button size="small" onClick={(event) => dismiss(event, reason)}>Dismiss</Button>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Box>
      )}
    </Container>
  );
}
